import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ATELIER, FONTS } from '../tokens';
import { useWorkspace } from '../WorkspaceContext';
import { TurnCard } from './TurnCard';
import { MemoryRibbon } from './MemoryRibbon';
import { Download, X } from 'lucide-react';

/**
 * Thread - scrollable unified conversation.
 *
 * Density:
 *   • centered max-width 760px
 *   • 28px gap between turns
 *   • left rail with modality badge + violet "spine" line
 *   • optional MemoryRibbon overlays (one per user turn that has a referenceTurnId)
 */
export function Thread() {
  const { conversation, expandedMedia, setExpandedMedia } = useWorkspace();
  const turns = conversation.turns;
  const scrollerRef = useRef(null);
  const trackRef = useRef(null);
  const stickRef = useRef(true);

  // Scroll bookkeeping - auto-stick to bottom unless user scrolled up
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const onScroll = () => {
      stickRef.current = el.scrollHeight - el.clientHeight - el.scrollTop < 140;
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    if (stickRef.current) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [turns.length, turns[turns.length - 1]?.status]);

  // Index turns for ribbon source lookup
  const turnIndex = new Map(turns.map(t => [t.id, t]));

  if (turns.length === 0) {
    return <ThreadAtrium />;
  }

  return (
    <div
      ref={scrollerRef}
      className="dgw-scroll"
      style={{
        flex: 1,
        overflowY: 'auto',
        position: 'relative',
        background: ATELIER.bg,
      }}
    >
      <div
        ref={trackRef}
        style={{
          position: 'relative',
          maxWidth: 1024,
          margin: '0 auto',
          padding: '32px 24px 36px',
          display: 'flex',
          flexDirection: 'column',
          gap: 24,
        }}
      >
        {/* Memory ribbons (rendered FIRST so they sit behind turns; positioning is
            absolute to the trackRef container) */}
        {turns.map((t, i) => {
          const ref = t.meta?.referenceTurnId;
          if (!ref || !turnIndex.has(ref)) return null;
          return (
            <MemoryRibbon
              key={`ribbon-${t.id}`}
              containerRef={trackRef}
              sourceTurnId={ref}
              targetTurnId={t.id}
            />
          );
        })}

        <AnimatePresence initial={false}>
          {turns.map((t, i) => (
            <TurnCard key={t.id} turn={t} isLast={i === turns.length - 1} />
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
}

/* ── ThreadAtrium - empty state inside an existing project ──── */
/**
 * Quiet, editorial. No halo, no marketing copy. Reads as a curator's note
 * pinned to the wall, not a hero panel.
 */
function ThreadAtrium() {
  const { conversation, projects, activeMode } = useWorkspace();
  const project = projects.find(p => p.id === conversation.projectId);
  const isSlides = activeMode === 'slides';
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (isMobile) {
    return <div style={{ flex: 1, background: ATELIER.bg }} />;
  }

  return (
    <div style={{
      flex: 1,
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'center',
      padding: '64px 32px 32px',
      background: ATELIER.bg,
      minHeight: 0,
    }}>
      <div style={{ width: '100%', maxWidth: 760 }}>
        {/* Top hairline + breadcrumb */}
        <div style={{
          display: 'flex', alignItems: 'baseline', gap: 14,
          marginBottom: 22,
        }}>
          <span style={{
            fontFamily: FONTS.mono,
            fontSize: 10, letterSpacing: '0.22em', fontWeight: 600,
            color: ATELIER.inkMuted,
            textTransform: 'uppercase',
          }}>{isSlides ? 'Slides Studio' : (project?.title || 'Quick chat')}</span>
          <span style={{
            flex: 1, height: 1,
            background: `linear-gradient(90deg, ${ATELIER.border} 0%, transparent 100%)`,
          }} />
          <span style={{
            fontFamily: FONTS.mono,
            fontSize: 10, letterSpacing: '0.22em', fontWeight: 600,
            color: ATELIER.inkFaint,
            textTransform: 'uppercase',
          }}>· {isSlides ? 'widescreen' : 'thread 01'}</span>
        </div>

        <h2 style={{
          fontFamily: FONTS.serif,
          fontSize: 'clamp(24px, 2.6vw, 30px)',
          fontWeight: 400,
          lineHeight: 1.18,
          letterSpacing: '-0.025em',
          color: ATELIER.ink,
          margin: '0 0 8px',
        }}>
          {isSlides ? 'Design a masterpiece.' : 'A blank canvas.'}
        </h2>
        <p style={{
          fontSize: 13.5, color: ATELIER.inkSoft, lineHeight: 1.6,
          margin: 0, letterSpacing: '-0.005em',
          maxWidth: 520,
        }}>
          {isSlides ? (
            <span>
              The composer below is tuned for widescreen presentation deck crafting. Submit a description, or attach PDF, Word, or TXT documents to build a beautifully sequenced presentation layout instantly.
            </span>
          ) : (
            <span>
              The composer below reads your prompt and routes it to chat, image,
              video, or music. Override anytime with <Code>/image</Code>,
              <Code>/video</Code>, <Code>/music</Code>.
            </span>
          )}
        </p>
      </div>
    </div>
  );
}

function Code({ children }) {
  return (
    <code style={{
      fontFamily: FONTS.mono, fontSize: 11.5,
      padding: '1px 6px', borderRadius: 4,
      color: ATELIER.accent,
      background: 'transparent',
      border: `1px solid ${ATELIER.borderAccent}`,
      margin: '0 2px',
      fontWeight: 500,
    }}>{children}</code>
  );
}
